import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import DeviceToolbar from "./DeviceToolbar";
import { useResponsiveScale } from "../../hooks/useResponsiveScale";
import { normalizeUrl } from "../../utils/urlUtils";
import { IFRAME_LOAD_TIMEOUT_MS, MAX_FIT_FRAME_HEIGHT, CATEGORY_LABELS } from "../../constants/previewConfig";

const LOAD_STATE = {
  LOADING: "loading",
  // Same-origin frame we could inspect, and it has a real document.
  LOADED: "loaded",
  // Cross-origin frame fired `load`. Browsers also fire `load` for frames
  // blocked by X-Frame-Options / frame-ancestors (Chrome swaps in an error
  // page), and cross-origin frames can't be inspected, so this is "something
  // loaded" — not proof the site rendered.
  UNVERIFIED: "unverified",
  // Frame is readable but still blank (about:blank) after `load`: the page
  // we asked for never rendered.
  BLOCKED: "blocked",
  SLOW: "slow",
};

// Lets previews behave like a normal tab (scripts, forms, logins, popups)
// while denying top-level navigation, so a framed site can't redirect the
// whole app away ("frame busting"). Note: the http(s)-only URL check below is
// the primary guard against script URLs; `allow-same-origin` is required for
// most real sites (cookies/storage) and means sandboxing alone can't be.
const IFRAME_SANDBOX =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads";

function inspectFrameLoad(iframe) {
  try {
    const href = iframe?.contentWindow?.location?.href;
    if (typeof href !== "string") return LOAD_STATE.UNVERIFIED;
    if (href === "" || href === "about:blank") return LOAD_STATE.BLOCKED;
    return LOAD_STATE.LOADED;
  } catch {
    // SecurityError: cross-origin (or a browser error page) — can't tell.
    return LOAD_STATE.UNVERIFIED;
  }
}

function useIsFullscreen(nodeRef) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const readViewport = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    const handleChange = () => {
      const active = Boolean(nodeRef.current) && document.fullscreenElement === nodeRef.current;
      setIsFullscreen(active);
      if (active) readViewport();
    };

    document.addEventListener("fullscreenchange", handleChange);
    window.addEventListener("resize", readViewport);
    return () => {
      document.removeEventListener("fullscreenchange", handleChange);
      window.removeEventListener("resize", readViewport);
    };
  }, [nodeRef]);

  return { isFullscreen, viewport };
}

// Cards mounted together fade in one after another; capped so a large
// selection never waits long for its last card.
const MAX_STAGGER_STEPS = 8;

function DeviceFrame({ device, url, rotated, globalZoom, globalReloadToken, onRotate, onRemove, index = 0 }) {
  // A per-device zoom only applies while the global zoom it was chosen
  // against is unchanged; changing the global zoom (or picking "Fit" here)
  // hands control back to the global setting.
  const [zoomOverride, setZoomOverride] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [loadState, setLoadState] = useState(LOAD_STATE.LOADING);
  const frameWrapperRef = useRef(null);
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);

  const zoom = zoomOverride && zoomOverride.base === globalZoom ? zoomOverride.value : globalZoom;

  // Defense in depth: never hand a non-http(s) URL to the iframe, whatever
  // path (storage, session import, props) it arrived by.
  const safeUrl = normalizeUrl(url).valid ? url : null;

  const width = rotated ? device.height : device.width;
  const height = rotated ? device.width : device.height;

  const { containerRef, scale } = useResponsiveScale(width, height, zoom, MAX_FIT_FRAME_HEIGHT);
  const { isFullscreen, viewport } = useIsFullscreen(frameWrapperRef);

  const fullscreenScale =
    isFullscreen && viewport.width > 0 && viewport.height > 0
      ? Math.min(viewport.width / width, viewport.height / height)
      : null;
  const innerScale = fullscreenScale ?? scale;
  const innerOffset = fullscreenScale
    ? {
        left: (viewport.width - width * fullscreenScale) / 2,
        top: (viewport.height - height * fullscreenScale) / 2,
      }
    : null;

  useEffect(() => {
    setLoadState(LOAD_STATE.LOADING);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setLoadState((prev) => (prev === LOAD_STATE.LOADING ? LOAD_STATE.SLOW : prev));
    }, IFRAME_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutRef.current);
  }, [url, device.id, reloadToken, globalReloadToken]);

  const handleIframeLoad = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setLoadState(inspectFrameLoad(iframeRef.current));
  }, []);

  const handleReload = useCallback(() => {
    setReloadToken((prev) => prev + 1);
  }, []);

  const handleZoomChange = useCallback(
    (value) => {
      setZoomOverride(value === "fit" ? null : { value: Number(value), base: globalZoom });
    },
    [globalZoom]
  );

  const handleOpenNewTab = useCallback(() => {
    if (safeUrl) window.open(safeUrl, "_blank", "noopener,noreferrer");
  }, [safeUrl]);

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard API unavailable — nothing else we can safely do here.
    }
  }, [url]);

  const handleFullscreen = useCallback(() => {
    const node = frameWrapperRef.current;
    if (!node?.requestFullscreen) return;
    try {
      // requestFullscreen() returns a promise that rejects when the browser
      // denies it; swallow that too, not just synchronous throws.
      const result = node.requestFullscreen();
      if (result && typeof result.catch === "function") result.catch(() => {});
    } catch {
      // Fullscreen can be denied by the browser; fail silently rather than
      // showing an error for a non-critical enhancement.
    }
  }, []);

  const showBlockedWarning = loadState === LOAD_STATE.SLOW || loadState === LOAD_STATE.BLOCKED;

  return (
    <div
      className={`device-card device-card--${device.category}`}
      style={{ "--stagger": Math.min(index, MAX_STAGGER_STEPS) }}
    >
      <DeviceToolbar
        device={device}
        rotated={rotated}
        zoom={zoom}
        onZoomChange={handleZoomChange}
        onRotate={() => onRotate(device.id)}
        onReload={handleReload}
        onRemove={() => onRemove(device.id)}
        onOpenNewTab={handleOpenNewTab}
        onCopyUrl={handleCopyUrl}
        onFullscreen={handleFullscreen}
      />

      <div className="device-meta">
        <span className="device-meta-name">
          {device.name} <span className="device-meta-brand">· {device.brand}</span>
        </span>
        <span className="device-meta-dims">
          {width} × {height} · {width > height ? "Landscape" : "Portrait"}
          {device.releaseYear ? ` · ${device.releaseYear}` : ""} · {CATEGORY_LABELS[device.category] || device.category}
        </span>
      </div>

      <div className="device-frame-container" ref={containerRef}>
        <div
          className="phone-frame"
          style={{ width: width * scale, height: height * scale }}
          ref={frameWrapperRef}
        >
          {loadState === LOAD_STATE.LOADING && (
            <div className="frame-status frame-status-loading" aria-live="polite">
              <div className="skeleton" aria-hidden="true">
                <span className="skeleton-bar skeleton-bar-hero" />
                <span className="skeleton-bar" />
                <span className="skeleton-bar skeleton-bar-short" />
                <span className="skeleton-bar" />
              </div>
              <span className="frame-status-label">
                <span className="spinner" aria-hidden="true" />
                Loading preview…
              </span>
            </div>
          )}

          <div
            className="phone-inner"
            style={{ width, height, transform: `scale(${innerScale})`, ...innerOffset }}
          >
            <iframe
              key={`${device.id}-${reloadToken}-${globalReloadToken}`}
              ref={iframeRef}
              src={safeUrl ?? undefined}
              title={`${device.name} preview`}
              width={width}
              height={height}
              sandbox={IFRAME_SANDBOX}
              onLoad={handleIframeLoad}
            />
          </div>

          {showBlockedWarning && (
            <div className="frame-status frame-status-warning" role="status">
              <p>
                {loadState === LOAD_STATE.BLOCKED
                  ? "The page didn't render inside the preview frame. "
                  : "This preview hasn't reported loading yet. "}
                The site may block iframe embedding (via <code>X-Frame-Options</code> or a{" "}
                <code>frame-ancestors</code> policy) — this isn't a confirmed diagnosis, just a
                possibility.
              </p>
              <div className="frame-status-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleReload}>
                  Retry
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleOpenNewTab}>
                  Open in new tab
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {loadState === LOAD_STATE.UNVERIFIED && (
        <p className="frame-hint">
          Blank preview? The site may block embedding.{" "}
          <button type="button" className="link-btn" onClick={handleOpenNewTab}>
            Open in new tab
          </button>
        </p>
      )}
    </div>
  );
}

function propsAreEqual(prev, next) {
  return (
    prev.device === next.device &&
    prev.url === next.url &&
    prev.rotated === next.rotated &&
    prev.globalZoom === next.globalZoom &&
    prev.globalReloadToken === next.globalReloadToken &&
    prev.onRotate === next.onRotate &&
    prev.onRemove === next.onRemove
  );
}

export default memo(DeviceFrame, propsAreEqual);
